import { useEffect, useState, type ReactNode } from 'react';
import { Search, RefreshCw, ChevronDown, ChevronUp, X, ExternalLink } from 'lucide-react';
import { fetchAdminProjects } from '../../lib/adminQueries';
import type { AdminProjectRow } from '../../types/admin';
import AdminProjectViewer from './AdminProjectViewer';

const STATUS_OPTIONS = ['all', 'in-progress', 'completed', 'archived'];

function DetailPanel({ project, onClose, onViewProject }: { project: AdminProjectRow; onClose: () => void; onViewProject: () => void }) {
  const cv = project.completion_validation;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3">
          <h3 className="font-semibold text-gray-900 truncate flex-1">{project.project_name}</h3>
          <button
            onClick={onViewProject}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors flex-shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View Project
          </button>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4 text-sm">
          <Section title="Project Identification">
            <Row label="Customer" value={project.customer_name} />
            <Row label="AHJ" value={project.ahj_jurisdiction} />
            <Row label="Status" value={project.status} />
            <Row label="Created" value={project.created_at ? new Date(project.created_at).toLocaleDateString() : null} />
            {project.completed_at && (
              <Row label="Completed" value={new Date(project.completed_at).toLocaleDateString()} />
            )}
          </Section>

          <Section title="Service &amp; Results">
            <Row label="Service Rating" value={project.service_rating_amps ? `${project.service_rating_amps} A` : null} />
            <Row label="Peak Demand" value={project.max_recorded_demand_kw ? `${project.max_recorded_demand_kw.toFixed(2)} kW` : null} />
            <Row label="Adequacy" value={project.pass_fail ?? 'Not evaluated'} highlight={project.pass_fail} />
          </Section>

          <Section title="AMI Data Summary">
            <Row label="Measurement Period" value={project.ami_measurement_period} />
            {project.ami_data_quality_notes && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Quality Notes</p>
                <p className="text-gray-700 bg-yellow-50 border border-yellow-100 rounded p-2 text-xs leading-relaxed">
                  {project.ami_data_quality_notes}
                </p>
              </div>
            )}
          </Section>

          {cv && (
            <Section title="Completion Validation Snapshot">
              <Row label="Validated At" value={cv.validatedAt ? new Date(cv.validatedAt).toLocaleString() : null} />
              <Row label="Blockers at completion" value={String(cv.blockerCount)} />
              <Row label="Warnings" value={String(cv.warningCount)} />
              <Row label="Advisories" value={String(cv.advisoryCount)} />
              <Row
                label="Warnings acknowledged"
                value={cv.warningAcknowledged ? 'Yes' : 'No'}
              />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | number | null | undefined; highlight?: string | null }) {
  const display = value != null && value !== '' ? String(value) : '—';
  const colorClass = highlight === 'PASS'
    ? 'text-green-700 font-semibold'
    : highlight === 'FAIL'
    ? 'text-red-700 font-semibold'
    : 'text-gray-900';
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-right ${colorClass}`}>{display}</span>
    </div>
  );
}

export default function AdminProjects() {
  const [projects, setProjects] = useState<AdminProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<AdminProjectRow | null>(null);
  const [viewingProject, setViewingProject] = useState<AdminProjectRow | null>(null);
  const [sortKey, setSortKey] = useState<keyof AdminProjectRow>('updated_at');
  const [sortAsc, setSortAsc] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setProjects(await fetchAdminProjects(statusFilter !== 'all' ? { status: statusFilter } : undefined));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (key: keyof AdminProjectRow) => {
    if (sortKey === key) setSortAsc(a => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortIcon = ({ k }: { k: keyof AdminProjectRow }) =>
    sortKey === k
      ? (sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
      : null;

  const filtered = projects
    .filter(p => {
      const q = search.toLowerCase();
      return !q || p.project_name.toLowerCase().includes(q) || (p.customer_name ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      return sortAsc
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
        <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or customer…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>
              {s === 'all' ? 'All statuses' : s === 'in-progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading projects…</div>
      ) : (
        <>
          {/* Mobile Table */}
          <div className="block md:hidden">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col />
                  <col className="w-24" />
                  <col className="w-24" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Project</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">User</th>
                    <th className="px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-8 text-center text-gray-400">No projects found</td>
                    </tr>
                  ) : filtered.map(p => (
                    <tr
                      key={p.id}
                      onClick={() => setSelected(p)}
                      className="cursor-pointer hover:bg-blue-50"
                    >
                      <td className="px-3 py-3">
                        <p className="text-gray-900 font-medium truncate">{p.project_name}</p>
                      </td>
                      <td className="px-2 py-3">
                        <p className="text-xs text-gray-500 truncate">{p.user_email || '—'}</p>
                      </td>
                      <td className="px-2 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          p.status === 'completed' ? 'bg-green-100 text-green-700'
                          : p.status === 'in-progress' ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status === 'in-progress' ? 'In Progress' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                {filtered.length} of {projects.length} project{projects.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {([
                      ['project_name', 'Project'],
                      ['user_email', 'User'],
                      ['status', 'Status'],
                      ['completed_at', 'Completed'],
                      ['pass_fail', 'Result'],
                    ] as [keyof AdminProjectRow, string][]).map(([k, label]) => (
                      <th
                        key={k}
                        onClick={() => toggleSort(k)}
                        className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs cursor-pointer hover:text-gray-700 select-none"
                      >
                        <span className="flex items-center gap-1">{label}<SortIcon k={k} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(p => (
                    <tr
                      key={p.id}
                      onClick={() => setSelected(p)}
                      className="hover:bg-blue-50 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{p.project_name}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{p.user_email ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.status === 'completed' ? 'bg-green-100 text-green-700'
                          : p.status === 'in-progress' ? 'bg-blue-100 text-blue-700'
                          : p.status === 'archived' ? 'bg-gray-100 text-gray-600'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {p.status === 'in-progress' ? 'In Progress' : p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {p.completed_at ? new Date(p.completed_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {p.pass_fail ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            p.pass_fail === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {p.pass_fail}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No projects found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              {filtered.length} of {projects.length} project{projects.length !== 1 ? 's' : ''}
            </div>
          </div>
        </>
      )}

      {selected && (
        <DetailPanel
          project={selected}
          onClose={() => setSelected(null)}
          onViewProject={() => {
            setViewingProject(selected);
            setSelected(null);
          }}
        />
      )}

      {viewingProject && (
        <AdminProjectViewer
          projectId={viewingProject.id}
          projectName={viewingProject.project_name}
          onClose={() => setViewingProject(null)}
        />
      )}
    </div>
  );
}
